import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { remindersService } from '@/services/reminders'
import type {
  ReminderFormData,
  ReminderFilters,
  ReminderStatus,
  CreateAutoReminderData,
} from '@/types/reminder'

// STORY-031: Hook pour gerer les relances

/**
 * Hook principal pour la liste des relances
 */
export function useReminders(filters?: ReminderFilters) {
  const queryClient = useQueryClient()

  const remindersQuery = useQuery({
    queryKey: ['reminders', filters],
    queryFn: () => remindersService.getAll(filters),
  })

  const createMutation = useMutation({
    mutationFn: (data: ReminderFormData) => remindersService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      queryClient.invalidateQueries({ queryKey: ['reminders-stats'] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ReminderFormData> }) =>
      remindersService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      queryClient.invalidateQueries({ queryKey: ['reminders-stats'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => remindersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      queryClient.invalidateQueries({ queryKey: ['reminders-stats'] })
    },
  })

  return {
    reminders: remindersQuery.data?.reminders ?? [],
    total: remindersQuery.data?.total ?? 0,
    isLoading: remindersQuery.isLoading,
    error: remindersQuery.error,
    refetch: remindersQuery.refetch,
    createReminder: createMutation.mutateAsync,
    updateReminder: updateMutation.mutateAsync,
    deleteReminder: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}

/**
 * Hook pour recuperer une relance par ID
 */
export function useReminderById(id: string) {
  return useQuery({
    queryKey: ['reminder', id],
    queryFn: () => remindersService.getById(id),
    enabled: !!id,
  })
}

/**
 * Hook pour les relances en attente (a envoyer)
 */
export function usePendingReminders() {
  return useQuery({
    queryKey: ['reminders', 'pending'],
    queryFn: () => remindersService.getPending(),
    refetchInterval: 60000, // Rafraichir toutes les minutes
  })
}

/**
 * Hook pour les statistiques des relances
 */
export function useRemindersStats() {
  return useQuery({
    queryKey: ['reminders-stats'],
    queryFn: () => remindersService.getStats(),
  })
}

/**
 * Hook pour changer le statut d'une relance
 */
export function useUpdateReminderStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ReminderStatus }) =>
      remindersService.updateStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['reminder', id] })
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      queryClient.invalidateQueries({ queryKey: ['reminders-stats'] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

/**
 * Hook pour envoyer une relance maintenant
 */
export function useSendReminderNow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => remindersService.sendNow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      queryClient.invalidateQueries({ queryKey: ['reminders-stats'] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

/**
 * Hook pour reporter une relance
 */
export function usePostponeReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, newDate }: { id: string; newDate: string }) =>
      remindersService.postpone(id, newDate),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['reminder', id] })
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
    },
  })
}

/**
 * Hook pour annuler une relance
 */
export function useCancelReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => remindersService.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      queryClient.invalidateQueries({ queryKey: ['reminders-stats'] })
    },
  })
}

/**
 * Hook pour escalader une relance
 */
export function useEscalateReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => remindersService.escalate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      queryClient.invalidateQueries({ queryKey: ['reminders-stats'] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

/**
 * Hook pour marquer une relance comme completee
 */
export function useCompleteReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => remindersService.markAsCompleted(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      queryClient.invalidateQueries({ queryKey: ['reminders-stats'] })
    },
  })
}

/**
 * Hook pour creer une relance automatique
 */
export function useCreateAutoReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAutoReminderData) => remindersService.createAutoReminder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      queryClient.invalidateQueries({ queryKey: ['reminders-stats'] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}
